import s from './OwnerPromotionPage.module.sass'

import React, { useState } from 'react'

import { ErrorBoundary } from '@shared/lib/components/ErrorBoundary'
import { Checkbox } from '@shared/ui/Form/Single/Checkbox'
import { Title } from '@shared/ui/Typography/Title'
import { Text } from '@shared/ui/Typography/Text'
import {
  PROMO_OPTIONS,
  STATICS,
  MAIN_FEATURE,
  MATERIALS,
  type PromotionState,
  type PromotionOption,
} from '@shared/types/promotion'
import { FloatingSummary } from '@shared/ui/Overlays/FloatingSummary'
import PromoVisualImg from '@images/ownerPersonalPage/Promo.png'
import MainFeatureVisualImg from '@images/ownerPersonalPage/Feature.png'
import StaticVisualImg from '@images/ownerPersonalPage/Statics.png'
import MaterialsVisualImg from '@images/ownerPersonalPage/Materials.png'
import { useAppSelector } from '@app/model/store'
import { SuccessfulRegistrationModal } from '@pages/OwnerLanding/RegistrationForm/SuccessfulRegistrationModal/SuccessfulRegistrationModal'

interface OwnerPromotionPageProps {
  slug: string
  restId: string
  currentPage: string
  data: any
  global_data: any
}

/**
 * @function OwnerPromotionPage - Продвижение
 */
const OwnerPromotionPage = (props: OwnerPromotionPageProps) => {
  const stateUser = useAppSelector((state) => state.global.user.data)
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false)
  const [isSending, setIsSending] = useState(false)

  const [promotionState, setPromotionState] = useState<PromotionState>({})

  const handleCheckboxChange =
    <K extends keyof PromotionState>(field: K) =>
    (option: NonNullable<PromotionState[K]>[number], multiselect = false) => {
      const currentSection = (promotionState[field] as (typeof option)[] | undefined) || []
      const newOptions = multiselect
        ? currentSection.includes(option)
          ? currentSection.filter((item) => item.id !== option.id)
          : [...currentSection, option]
        : currentSection.includes(option)
        ? []
        : [option]

      setPromotionState((prev) => ({
        ...prev,
        [field]: newOptions,
      }))
    }

  const renderSection = (
    title: React.ReactNode,
    description: React.ReactNode,
    options: PromotionOption[],
    field: keyof PromotionState,
    imageSrc: string,
    multiselect = false,
  ) => (
    <>
      <Title tag='h3' sizes='h5'>
        {title}
      </Title>

      <div className={s['block-content']}>
        <div className={s['block-column']}>
          <Text sizes='M' className={s['description-card']}>
            {description}
          </Text>

          <div className={s['options-list']}>
            {options.map((option) => (
              <Text sizes='M' key={option.id} className={s['option-card']}>
                <div className={s['checkbox-group']}>
                  <Checkbox
                    mode='checkbox'
                    option={{
                      id: option.id,
                      name: option.id,
                    }}
                    checked={promotionState[field]?.includes(option)}
                    onChange={() => handleCheckboxChange(field)(option, multiselect)}
                  />

                  <span className={s['option-label']} onClick={() => handleCheckboxChange(field)(option, multiselect)}>
                    {field === 'materials' ? option.longLabel : option.label}
                  </span>
                </div>

                <>{option.price.toLocaleString('ru-RU')} ₽</>
              </Text>
            ))}
          </div>
        </div>

        <div className={s['visual']}>
          <img srcSet={`${imageSrc} 2x`} alt='Промо визуал' />
        </div>
      </div>
    </>
  )

  return (
    <ErrorBoundary>
      {isSuccessModalOpen && (
        <SuccessfulRegistrationModal email={stateUser?.email || ''} onClose={() => setIsSuccessModalOpen(false)} />
      )}

      <div className={s['page-content']}>
        <div className={s['big-block']}>
          {renderSection(
            'Промо',
            <>
              Показы в динамике с выделением цветом и&nbsp;пометкой «Промо»: на 1-м и 7-м местах в&nbsp;подборках «Выбор
              Афиши», на 1-м месте в&nbsp;списках ресторанов и в блоках «Места рядом» и «Похожие заведения»
            </>,
            PROMO_OPTIONS,
            'promoOptions',
            PromoVisualImg.src,
          )}
        </div>

        <div className={s['medium-block']}>
          {renderSection(
            'Фичер на главной',
            <>
              Демонстрация карточки ресторана в&nbsp;слайдере&nbsp;на&nbsp;главной странице «Афиши Рестораны» в городе
              заведения
            </>,
            MAIN_FEATURE,
            'mainFeature',
            MainFeatureVisualImg.src,
          )}
        </div>

        <div className={s['medium-block']}>
          {renderSection(
            'Статика в подборке',
            <>
              Демонстрация карточки ресторана
              <br />
              на 1-м месте в одной из тематических подборок&nbsp;«Выбор Афиши»
            </>,
            STATICS,
            'statics',
            StaticVisualImg.src,
          )}
        </div>

        <div className={s['big-block']}>
          {renderSection(
            'Редакционные материалы',
            <>
              Партнёрский материал на сайте
              <br />
              (статья/новость/интервью) и участие
              <br />в подборке «Планы на неделю»
            </>,
            MATERIALS,
            'materials',
            MaterialsVisualImg.src,
            true,
          )}
        </div>
      </div>

      <FloatingSummary
        title='Услуги продвижения'
        selectionState={promotionState}
        setSelectionState={setPromotionState}
        isLoading={isSending}
        onSubmit={() => {
          setIsSending(true)

          setTimeout(() => {
            setIsSuccessModalOpen(true)
            setIsSending(false)
            setPromotionState({})
          }, 1000)
        }}
      />
    </ErrorBoundary>
  )
}

export default OwnerPromotionPage
